import { createContext, useContext, useEffect, useState } from "react";
import {
  fetchCartAPI,
  addToCartAPI,
  updateCartItemAPI,
  removeCartItemAPI,
  clearUserCart
} from "../api/cart";
import { toast } from "react-toastify";

export const CartContext = createContext();

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [user, setUser] = useState(() =>
    JSON.parse(localStorage.getItem("user"))
  );
  const token = localStorage.getItem("token");

  useEffect(() => {
  if (user) {
    fetchCart();
    mergeGuestCartToUserCart();
  } else {
    const guestCart = JSON.parse(localStorage.getItem("guestCart") || "[]");
    setCart(guestCart);
  }
}, [user]);

  const fetchCart = async () => {
    if (!user) return;
    try {
      const res = await fetchCartAPI(user._id, token);
      setCart(res.data?.data || []);
    } catch {
      setCart([]);
    }
  };

  const addToCart = async (product, quantity = 1) => {
    if (!user) {
      const guestCart = JSON.parse(localStorage.getItem("guestCart") || "[]");

      const alreadyInCart = guestCart.some((item) => item._id === product._id);
      if (alreadyInCart) {
        toast.info("Product already in cart");
        return;
      }

      guestCart.push({ ...product, pieces: quantity });
      localStorage.setItem("guestCart", JSON.stringify(guestCart));
      setCart(guestCart);
      toast.success("Added to cart");
      return;
    }

    // For logged-in user: Check if product already in cart
    const alreadyInCart = cart.some(
      (item) => item.product_id?._id === product._id
    );

    if (alreadyInCart) {
      toast.info("Product already in cart");
      return;
    }

    const payload = {
      product_id: product._id,
      user_id: user._id,
      quantity: product.quantity || 1,
      price: product.price,
      shipping_charge: product.shipping_charge || 0,
      pieces: quantity,
      product_name: product.name,
      admin: product.admin || user.name || false,
      product_image: product.image,
      product_category: product.category,
      product_description: product.description,
      date_time: new Date().toISOString(),
    };

    try {
      await addToCartAPI(payload, token);
      toast.success("Added to cart");
      fetchCart();
    } catch {
      toast.error("Failed to add to cart");
    }
  };

  const mergeGuestCartToUserCart = async () => {
  if (!user) return;

  const guestCart = JSON.parse(localStorage.getItem("guestCart") || "[]");
  if (!guestCart.length) return;

  localStorage.removeItem("guestCart");

  try {
    const res = await fetchCartAPI(user._id, token);
    const serverCart = res.data?.data || [];

    const serverProductIds = new Set(
      serverCart.map(item => 
        typeof item.product_id === 'object' ? item.product_id._id : item.product_id
      )
    );

    const itemsToAdd = guestCart.filter(item => !serverProductIds.has(item._id || item.product_id));

    for (const item of itemsToAdd) {
      const payload = {
        product_id: item._id || item.product_id,
        user_id: user._id,
        quantity: item.quantity || 1,
        price: item.price,
        shipping_charge: item.shipping_charge || 0,
        pieces: item.pieces || 1,
        product_name: item.name || item.product_name || '',
        admin: item.admin || user.name || false,
        product_image: item.image || item.product_image || '',
        product_category: item.category || '',
        product_description: item.description || '',
        date_time: item.date_time || new Date().toISOString(),
      };

      await addToCartAPI(payload, token);
    }

    await fetchCart();
  } catch (err) {
    console.error("Failed to merge cart:", err);
  }
};


  const updateCartItem = async (cartItemId, pieces) => {
    if (!user) {
      const guestCart = JSON.parse(localStorage.getItem("guestCart") || "[]");
      const updated = guestCart.map((item) =>
        item._id === cartItemId ? { ...item, pieces } : item
      );
      localStorage.setItem("guestCart", JSON.stringify(updated));
      setCart(updated);
      return;
    }

    try {
      await updateCartItemAPI(cartItemId, { pieces }, token);
      toast.success("Cart Updated");
      fetchCart();
    } catch {
      toast.error("Failed to Update");
    }
  };

  const removeFromCart = async (cartItemId) => {
    if (!user) {
      const guestCart = JSON.parse(
        localStorage.getItem("guestCart") || "[]"
      ).filter((item) => item._id !== cartItemId);
      localStorage.setItem("guestCart", JSON.stringify(guestCart));
      setCart(guestCart);
      toast.success("Product Removed");
      return;
    }

    try {
      await removeCartItemAPI(cartItemId, token);
      toast.success("Product Removed");
      fetchCart();
    } catch {}
  };

  const clearCart = async () => {
  if (!user) {
    localStorage.removeItem("guestCart");
    setCart([]);
    return;
  }

  try {
    await clearUserCart(user._id, token);
    setCart([]);
  } catch (err) {
    console.error("Error clearing cart:", err);
  }
};



  const getTotalItems = () => {
  return cart.reduce((total, item) => total + (item.pieces || item.quantity || 1), 0);
};

  return (
    <CartContext.Provider
      value={{
        cart,
        setCart,
        addToCart,
        updateCartItem,
        removeFromCart,
        clearCart,
        fetchCart,
        getTotalItems
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => useContext(CartContext);
